/**
 * Everything about the app that is not one of its screens, behind one button.
 *
 * The theme picker and the motion toggle used to stand in the masthead, on the
 * gradient, on every screen — which is a lot of permanent chrome for two
 * controls somebody touches twice a year, and it pushed the first card of the
 * home screen down the page. They are here now, with the admin link and the
 * two ways out, and the masthead is back to saying who you are.
 *
 * What that costs is the demonstration: picking a theme used to re-tint the
 * band the picker was standing on. So this is a sheet over the page rather
 * than a full-bleed takeover — the masthead is still on the screen behind it,
 * and choosing Sunset still cross-fades the hero in front of somebody who is
 * looking at it. `src/index.css` draws it, and it comes up on the same
 * `overlay-up` keyframe the receipt viewer and the scanner do, so the one
 * `--moving` switch flattens it to an opacity fade with everything else.
 *
 * ## The ways out
 *
 * The Close button, the Escape key, and the scrim. All three are the same
 * thing, and there are three of them because this is a modal over a page whose
 * controls are still visible behind it: somebody who opened it by accident
 * should not have to find the one pixel that dismisses it.
 *
 * The admin link is a courtesy here exactly as it was in the masthead —
 * `/admin` is a path anybody can type, and `server/admin.ts` refuses the
 * request whatever the SPA decided to draw.
 */

import { useEffect, useRef } from 'react'
import type { MotionPreference } from '../lib/motion'
import { MotionToggle } from './MotionToggle'
import { ThemePicker } from './ThemePicker'

export function SettingsPanel({
  motion,
  isAdmin,
  busy,
  onAdmin,
  onSignOut,
  onClose,
}: {
  motion: MotionPreference
  isAdmin: boolean
  busy: boolean
  onAdmin: () => void
  onSignOut: (everywhere: boolean) => void
  onClose: () => void
}) {
  const closeButton = useRef<HTMLButtonElement>(null)

  /**
   * The focus goes into the sheet when it opens and back where it came from
   * when it closes — which is the settings button, so pressing Escape leaves
   * somebody on a keyboard exactly where they started rather than at the top
   * of the document.
   */
  useEffect(() => {
    const opener = document.activeElement
    closeButton.current?.focus()
    return () => {
      if (opener instanceof HTMLElement) opener.focus()
    }
  }, [])

  // Escape, wherever the focus went inside the sheet.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div className="settings">
      {/*
        The scrim is the third way out and not a control: it carries no label
        and nothing a screen reader would read, because the dialog beside it
        already offers Close, and a nameless button in the tab order is worse
        than no button at all.
      */}
      <div className="settings__scrim" aria-hidden="true" onClick={onClose} />
      <div
        className="settings__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="settings-title"
      >
        <div className="settings__bar">
          <h2 className="settings__title" id="settings-title">
            Settings
          </h2>
          <button ref={closeButton} type="button" className="button--quiet" onClick={onClose}>
            Close
          </button>
        </div>

        <div className="settings__body">
          <div className="settings__row">
            <ThemePicker />
          </div>
          <div className="settings__row">
            <MotionToggle preference={motion} />
          </div>

          <p className="settings__actions">
            {isAdmin && (
              <button type="button" className="button--quiet" onClick={onAdmin}>
                Admin
              </button>
            )}
            <button
              type="button"
              className="button--quiet"
              disabled={busy}
              onClick={() => onSignOut(false)}
            >
              Sign out
            </button>
            <button
              type="button"
              className="button--quiet"
              disabled={busy}
              onClick={() => onSignOut(true)}
            >
              Sign out everywhere
            </button>
          </p>
        </div>
      </div>
    </div>
  )
}
